/** Resolve the current theme's object-position lengths without transformed DOM bounds. */
function positionLength(value, freeSpace) {
  if (value === 'left' || value === 'top') return 0;
  if (value === 'right' || value === 'bottom') return freeSpace;
  if (value === 'center') return freeSpace / 2;
  if (value.endsWith('%')) return (parseFloat(value) / 100) * freeSpace;
  if (value.endsWith('px') || value === '0') return parseFloat(value);
  const calc = value.match(/^calc\(\s*([\d.-]+)%\s*([+-])\s*([\d.]+)px\s*\)$/);
  if (calc) return (Number(calc[1]) / 100) * freeSpace + (calc[2] === '-' ? -1 : 1) * Number(calc[3]);
  return freeSpace / 2;
}

function objectPosition(position, freeX, freeY) {
  const values = position.match(/calc\([^)]*\)|\S+/g) || ['50%', '50%'];
  if (values.length > 2) {
    let x = freeX / 2;
    let y = freeY / 2;
    for (let index = 0; index < values.length; index += 1) {
      const edge = values[index];
      const isX = edge === 'left' || edge === 'right';
      const isY = edge === 'top' || edge === 'bottom';
      if (!isX && !isY) continue;
      const space = isX ? freeX : freeY;
      const next = values[index + 1];
      const hasOffset = next && !/^(left|right|top|bottom|center)$/.test(next);
      const offset = hasOffset ? positionLength(next, space) : 0;
      if (hasOffset) index += 1;
      const value = edge === 'right' || edge === 'bottom' ? space - offset : offset;
      if (isX) x = value;
      else y = value;
    }
    return { x, y };
  }
  if (values.length === 1) {
    if (values[0] === 'top' || values[0] === 'bottom') values.unshift('50%');
    else values.push('50%');
  }
  if (values[0] === 'top' || values[0] === 'bottom') values.reverse();
  return { x: positionLength(values[0], freeX), y: positionLength(values[1], freeY) };
}

/** Image artwork coordinates inside the untransformed content box. */
export function artworkRect(width, height, naturalWidth, naturalHeight, fit = 'contain', position = '50% 50%') {
  if (Math.min(width, height, naturalWidth, naturalHeight) <= 0) return null;
  const containScale = Math.min(width / naturalWidth, height / naturalHeight);
  let scale = containScale;
  if (fit === 'cover') scale = Math.max(width / naturalWidth, height / naturalHeight);
  if (fit === 'none') scale = 1;
  if (fit === 'scale-down') scale = Math.min(1, containScale);
  const artWidth = fit === 'fill' ? width : naturalWidth * scale;
  const artHeight = fit === 'fill' ? height : naturalHeight * scale;
  return { ...objectPosition(position, width - artWidth, height - artHeight), width: artWidth, height: artHeight };
}

if (typeof window !== 'undefined' && !customElements.get('wp-native-art-overlay')) {
  customElements.define('wp-native-art-overlay', class extends HTMLElement {
    connectedCallback() {
      this.host = this.parentElement;
      this.plane = this.querySelector(':scope > .wp-native-art-plane');
      if (!this.host || !this.plane) return;
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.measure();
        });
      };
      this.resizeObserver = new ResizeObserver(this.schedule);
      this.imageObserver = new MutationObserver(this.schedule);
      this.childrenObserver = new MutationObserver(() => this.bindImage());
      this.childrenObserver.observe(this.host, { childList: true });
      this.resizeObserver.observe(this.host);
      window.addEventListener('resize', this.schedule, { passive: true });
      this.bindImage();
    }

    bindImage() {
      const next = [...this.host.children].find((child) => child instanceof HTMLImageElement);
      if (next === this.image) return;
      if (this.image) {
        this.image.removeEventListener('load', this.schedule);
        this.resizeObserver.unobserve(this.image);
      }
      this.imageObserver.disconnect();
      this.image = next;
      this.removeAttribute('data-ready');
      if (!next) return;
      // Native zoom replaces the original img with a high-resolution img.
      next.addEventListener('load', this.schedule);
      this.resizeObserver.observe(next);
      this.imageObserver.observe(next, { attributes: true, attributeFilter: ['style', 'class', 'width', 'height', 'src', 'srcset'] });
      this.schedule();
    }

    measure() {
      const image = this.image;
      if (!image?.isConnected || !image.complete || !image.naturalWidth) {
        this.removeAttribute('data-ready');
        return;
      }
      const css = getComputedStyle(image);
      const value = (key) => parseFloat(css[key]) || 0;
      const paddingX = value('paddingLeft') + value('paddingRight');
      const paddingY = value('paddingTop') + value('paddingBottom');
      const borderX = value('borderLeftWidth') + value('borderRightWidth');
      const borderY = value('borderTopWidth') + value('borderBottomWidth');
      const borderBox = css.boxSizing === 'border-box';
      const width = value('width') - (borderBox ? paddingX + borderX : 0);
      const height = value('height') - (borderBox ? paddingY + borderY : 0);
      const art = artworkRect(width, height, image.naturalWidth, image.naturalHeight, css.objectFit, css.objectPosition);
      if (!art) {
        this.removeAttribute('data-ready');
        return;
      }
      Object.assign(this.style, {
        left: `${image.offsetLeft}px`, top: `${image.offsetTop}px`,
        width: `${width + paddingX + borderX}px`, height: `${height + paddingY + borderY}px`,
      });
      this.style.setProperty('--wp-native-image-transform', css.transform);
      this.style.setProperty('--wp-native-image-origin', css.transformOrigin);
      Object.assign(this.plane.style, {
        left: `${art.x + value('paddingLeft') + value('borderLeftWidth')}px`,
        top: `${art.y + value('paddingTop') + value('borderTopWidth')}px`,
        width: `${art.width}px`, height: `${art.height}px`,
      });
      this.setAttribute('data-ready', '');
    }

    disconnectedCallback() {
      this.image?.removeEventListener('load', this.schedule);
      this.resizeObserver?.disconnect();
      this.imageObserver?.disconnect();
      this.childrenObserver?.disconnect();
      window.removeEventListener('resize', this.schedule);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.image = null;
      this.removeAttribute('data-ready');
    }
  });
}
